import React, {FC, ReactNode, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateAvailabilityStatusesEnum} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {collectEnumKeys} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {getHasTeammateBusyStatusFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemInboxDot} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemInboxDot';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {Tooltip} from '../../../../../libs/shared/ui/src/components/tooltips/tooltip';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FilterApplyFooter} from '../../../../components/common/filters/common/filterApplyFooter';
import {computeTeammateAvailabilityValues} from '../../../../components/teammates/teammateAvailabilityHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';

/*
 * Constants.
 */

// TODO(ldemarne): Use TeammateAvailabilityStatusesEnum when `teammate-busy-status` is fully rolled out.
export enum SelectableStatusesEnum {
  AVAILABLE = 'available',
  OFFLINE_OR_BUSY = 'offline_busy', // This is offline or busy depending on the `teammate-busy-status` feature-flag.
  OUT_OF_OFFICE = 'out_of_office',
}

export const LIVE_DASHBOARD_SELECTABLE_STATUSES_COUNT = collectEnumKeys(SelectableStatusesEnum).length;

/*
 * Props.
 */

interface StatusDropdownProps {
  selection: Set<SelectableStatusesEnum>;

  onBackClick: () => void;
  onStatusesSelection: (selection: Set<SelectableStatusesEnum>) => void;

  popoverProps: PopoverTransferProps;
}

/*
 * Component.
 */

export const LiveDashboardAssignedByTeammateStatusDropdown: FC<StatusDropdownProps> = (props) => {
  const {popoverProps, onStatusesSelection, onBackClick} = props;

  const hasTeammateBusyStatus = useAppSelector(getHasTeammateBusyStatusFF);
  const theme = useUserTheme();

  const [selection, setSelection] = useState(new Set(props.selection));
  const availableValues = computeTeammateAvailabilityValues(TeammateAvailabilityStatusesEnum.AVAILABLE);
  const busyValues = computeTeammateAvailabilityValues(TeammateAvailabilityStatusesEnum.BUSY);
  const outOfOfficeValues = computeTeammateAvailabilityValues(TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE);

  const toggleSetValue = (value: SelectableStatusesEnum) => {
    setSelection((previousSelection) => {
      if (previousSelection.has(value)) {
        previousSelection.delete(value);
      } else {
        previousSelection.add(value);
      }

      return new Set(previousSelection);
    });
  };

  const renderDropdownItem = (
    status: SelectableStatusesEnum,
    color: PaletteColorsEnum,
    label: ReactNode,
    tooltip: ReactNode,
  ) => (
    <DropdownItem onSelect={() => toggleSetValue(status)}>
      <DropdownItemInboxDot inbox={{color: theme.palette[color].shade40}} />
      <DropdownItemContent
        isChecked={selection.has(status)}
        renderTooltip={(tooltipProps) => <Tooltip {...tooltipProps}>{tooltip}</Tooltip>}
      >
        {label}
      </DropdownItemContent>
      <DropdownItemCheckbox isChecked={selection.has(status)} />
    </DropdownItem>
  );

  // TODO(ldemarne): Remove when `teammate-busy-status` is fully rolled out.
  const renderOfflineBusyDropdownItem = () => {
    if (!hasTeammateBusyStatus) {
      return renderDropdownItem(
        SelectableStatusesEnum.OFFLINE_OR_BUSY,
        PaletteColorsEnum.GREY,
        <Trans id="live-dashboard-assigned-by-teammate-offline-label">Offline</Trans>,
        <Trans id="live-dashboard-assigned-by-teammate-offline-explanation">
          Teammates currently offline but still available for new conversation assignments.
        </Trans>,
      );
    }

    return renderDropdownItem(
      SelectableStatusesEnum.OFFLINE_OR_BUSY,
      busyValues.color,
      busyValues.text,
      <Trans id="live-dashboard-assigned-by-teammate-busy-explanation">
        Teammates unavailable for auto-assignment because they have manually set their status to
        &quot;Busy&quot;.
      </Trans>,
    );
  };

  return (
    <Dropdown {...popoverProps} width={250}>
      <DropdownHeader
        title={<Trans id="live-dashboard-assigned-by-teammate-statuses-title">Filter by status</Trans>}
        leftIcon={
          <IconButton
            iconName="chevronLeftSmall"
            label={<Trans id="live-dashboard-assigned-by-teammate-statuses-back">Back</Trans>}
            onClick={onBackClick}
          />
        }
      />
      <DropdownList>
        {renderDropdownItem(
          SelectableStatusesEnum.AVAILABLE,
          availableValues.color,
          availableValues.text,
          <Trans id="live-dashboard-assigned-by-teammate-available-explanation">
            Teammates currently online and available for new conversation assignments.
          </Trans>,
        )}
        {renderOfflineBusyDropdownItem()}
        {renderDropdownItem(
          SelectableStatusesEnum.OUT_OF_OFFICE,
          outOfOfficeValues.color,
          outOfOfficeValues.text,
          <Trans id="live-dashboard-assigned-by-teammate-ooo-explanation">
            Teammates unavailable for auto-assignment because out of shift, on a scheduled time off, or
            manually set their status to &quot;Out of office.&quot;
          </Trans>,
        )}
      </DropdownList>
      <FilterApplyFooter
        isApplyDisabled={selection.size === 0}
        onApplyClicked={() => {
          onStatusesSelection(selection);
          popoverProps.onRequestClose();
        }}
        onCancelClicked={popoverProps.onRequestClose}
        renderApplyDisabledTooltip={() => (
          <Trans id="live-dashboard-assigned-by-teammate-one-status-required">
            At least one selected status required
          </Trans>
        )}
      />
    </Dropdown>
  );
};
